import React, { useEffect, useRef, useState } from 'react';
import { Modal, Form, InputNumber, Switch, Button, Space, Typography, message } from 'antd';
import { eventoService } from '../../services/api';
import { handleApiError } from '../../utils/apiError';

const { Text } = Typography;

// Configuracao de capacidade + abertura/fechamento de inscricoes da
// Sessao (Fase B.3b) -- expoe alterar_capacidade_sessao/abrir_inscricoes_
// sessao/fechar_inscricoes_sessao (app/services/evento_inscricao_service.py)
// sem duplicar nenhuma regra de negocio aqui: capacidade abaixo da
// ocupacao continua bloqueada pelo backend (409), este modal so mostra o
// erro amigavel que vier.
//
// Formulario com save EXPLICITO (ajuste de UX): o switch de abertura e o
// campo de capacidade alteram apenas estado LOCAL do formulario -- nenhum
// dos dois dispara chamada de API sozinho. So "Salvar alteracoes" grava,
// comparando contra os valores originais carregados na abertura do modal
// e chamando SOMENTE os endpoints (ja existentes) cujo valor realmente
// mudou.
//
// Capacidade: campo vazio = ilimitada (NULL). 0 = ZERO vagas literal --
// nunca interpretado como ilimitado (mesma regra do backend).
//
// LIMITACAO CONHECIDA (sem gambiarra de rollback): capacidade e abertura
// sao dois endpoints PUT independentes no backend, sem transacao
// combinando os dois. Se ambos os campos mudarem, a capacidade e salva
// PRIMEIRO (e a operacao com falha mais provavel -- 409 ao reduzir abaixo
// da ocupacao atual; abrir/fechar so falham se Evento/Sessao mudaram de
// status enquanto o modal estava aberto, bem mais raro). Se a capacidade
// falhar, NENHUMA chamada de abertura e feita -- nenhum estado parcial e
// criado. Se a capacidade for salva com sucesso e a chamada de abertura
// falhar em seguida, o resultado e um estado PARCIAL real (capacidade
// nova, abertura antiga) -- isso e reportado explicitamente ao usuario
// (nunca "sucesso" indevido), a tela e atualizada para refletir o estado
// real do banco em ambos os casos, e o modal permanece aberto com a
// capacidade ja marcada como salva -- um novo clique em "Salvar
// alteracoes" tenta apenas a parte que ainda falta.
const SessaoInscricoesConfigModal = ({ open, evento, sessao, podeEditar, onClose, onAtualizado }) => {
  const [form] = Form.useForm();
  const [salvando, setSalvando] = useState(false);
  const originalRef = useRef({ capacidade_total: null, inscricoes_abertas: false });

  useEffect(() => {
    if (open && sessao) {
      const valoresIniciais = {
        capacidade_total: sessao.capacidade_total ?? null,
        inscricoes_abertas: !!sessao.inscricoes_abertas,
      };
      form.setFieldsValue(valoresIniciais);
      originalRef.current = valoresIniciais;
    }
  }, [open, sessao, form]);

  const capacidadeAtual = Form.useWatch('capacidade_total', form);
  const aberturaAtual = Form.useWatch('inscricoes_abertas', form);

  const capacidadeMudou = () => {
    const atual = capacidadeAtual === undefined ? null : capacidadeAtual;
    return atual !== originalRef.current.capacidade_total;
  };
  const aberturaMudou = () => aberturaAtual !== originalRef.current.inscricoes_abertas;
  const houveMudanca = capacidadeMudou() || aberturaMudou();

  const handleSalvar = async (values) => {
    if (!houveMudanca) {
      onClose?.();
      return;
    }

    setSalvando(true);
    let sessaoAtualizada = sessao;
    try {
      if (capacidadeMudou()) {
        const capacidade = values.capacidade_total === undefined ? null : values.capacidade_total;
        const resp = await eventoService.alterarCapacidadeSessao(evento.id, sessao.id, capacidade);
        sessaoAtualizada = resp.data;
        originalRef.current = { ...originalRef.current, capacidade_total: capacidade };
      }

      if (aberturaMudou()) {
        const resp = values.inscricoes_abertas
          ? await eventoService.abrirInscricoesSessao(evento.id, sessao.id)
          : await eventoService.fecharInscricoesSessao(evento.id, sessao.id);
        sessaoAtualizada = resp.data;
        originalRef.current = { ...originalRef.current, inscricoes_abertas: values.inscricoes_abertas };
      }

      message.success('Alterações salvas com sucesso');
      onAtualizado?.(sessaoAtualizada);
      onClose?.();
    } catch (err) {
      // Reflete o estado REAL (possivelmente parcial) na tela em vez de
      // manter dados desatualizados -- nunca finge sucesso total. O
      // modal permanece aberto: originalRef ja foi avancado para a parte
      // que teve sucesso (se houve), entao um novo "Salvar alteracoes"
      // tenta somente o que ainda falta.
      onAtualizado?.(sessaoAtualizada);
      handleApiError(err, 'Erro ao salvar alterações');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal
      title={sessao ? `Inscrições — ${sessao.nome}` : 'Inscrições'}
      open={open}
      onCancel={salvando ? undefined : onClose}
      footer={null}
      destroyOnClose
      maskClosable={!salvando}
      closable={!salvando}
    >
      <Form form={form} layout="vertical" onFinish={handleSalvar}>
        <div style={{ marginBottom: 20 }}>
          <Text strong>Inscrições da sessão</Text>
          <div style={{ marginTop: 8 }}>
            <Form.Item name="inscricoes_abertas" valuePropName="checked" noStyle>
              <Switch
                disabled={!podeEditar || salvando}
                checkedChildren="Abertas"
                unCheckedChildren="Fechadas"
              />
            </Form.Item>
          </div>
          <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
            Fechar inscrições impede novas solicitações, mas não cancela inscrições já existentes.
            As alterações só serão aplicadas ao clicar em &quot;Salvar alterações&quot;.
          </Text>
        </div>

        <Form.Item
          name="capacidade_total"
          label="Capacidade"
          extra="Deixe em branco para capacidade ilimitada. 0 = nenhuma vaga."
        >
          <InputNumber min={0} style={{ width: '100%' }} placeholder="Ilimitada" disabled={!podeEditar || salvando} />
        </Form.Item>

        {podeEditar && (
          <Space>
            <Button type="primary" htmlType="submit" loading={salvando} disabled={!houveMudanca}>
              Salvar alterações
            </Button>
            <Button onClick={onClose} disabled={salvando}>
              Cancelar
            </Button>
          </Space>
        )}
      </Form>
    </Modal>
  );
};

export default SessaoInscricoesConfigModal;
